import { FIXTURES } from "../domain/fixtures";
import type { BuyerFundingReview, ReceiptPackage } from "../domain/types";
import { buildReceiptPackage } from "../domain/receipt";
import type { ReceiptExportManifest, StoredCaseManifest } from "../domain/r2Pipeline";
import type {
  BuyerFlowProgressSnapshot,
  EscrowPaymentRequestRecord
} from "../domain/buyerFlowProgress";
import type { PublishedFileSummary, PublishBuyerFileInput } from "../domain/publishedFiles";
import type { BuyerFundingFlowSession } from "../flows/buyerFunding/types";
import { apiBackendAvailable } from "./backend";
import { apiRequestHeaders } from "./headers";

export const STORAGE_KEY = "buyer-funding-reviews";

export async function listReviews(): Promise<BuyerFundingReview[]> {
  if (await apiBackendAvailable()) {
    const response = await fetch("/api/reviews", { headers: apiRequestHeaders() });
    if (response.ok && response.headers.get("content-type")?.includes("application/json")) {
      return (await response.json()) as BuyerFundingReview[];
    }
    if (response.status === 401) {
      throw new Error("Office API requires authentication. Use npm run dev (Wrangler + Vite) or sign in via Access.");
    }
  }
  return readReviewsFromLocalStorage();
}

export async function saveReview(review: BuyerFundingReview): Promise<BuyerFundingReview> {
  const updated = { ...review, updatedAt: new Date().toISOString() };
  if (await apiBackendAvailable()) {
    const response = await fetch("/api/reviews", {
      method: "POST",
      headers: apiRequestHeaders(true),
      body: JSON.stringify(updated)
    });
    if (response.ok && response.headers.get("content-type")?.includes("application/json")) {
      const saved = (await response.json()) as BuyerFundingReview;
      cacheReviewsLocally([saved, ...(await readReviewsFromLocalStorage()).filter((r) => r.id !== saved.id)]);
      return saved;
    }
    if (response.status === 401) {
      throw new Error("Cannot save review without office authentication.");
    }
  }
  const next = [updated, ...(await readReviewsFromLocalStorage()).filter((candidate) => candidate.id !== updated.id)];
  cacheReviewsLocally(next);
  return updated;
}

export async function exportReview(review: BuyerFundingReview): Promise<ReceiptPackage> {
  if (await apiBackendAvailable()) {
    const response = await fetch(`/api/reviews/${review.id}/export`, {
      method: "POST",
      headers: apiRequestHeaders(true),
      body: JSON.stringify(review)
    });
    if (response.ok && response.headers.get("content-type")?.includes("application/json")) {
      return await response.json();
    }
    if (response.status === 401) {
      throw new Error("Cannot export receipt without office authentication.");
    }
  }
  return buildReceiptPackage(review);
}

export async function persistBuyerFlowProgress(snapshot: BuyerFlowProgressSnapshot): Promise<void> {
  if (typeof localStorage !== "undefined") {
    const store = JSON.parse(localStorage.getItem("buyer-funding-flow-progress") || "{}") as Record<
      string,
      BuyerFlowProgressSnapshot
    >;
    store[snapshot.fileCode.toUpperCase()] = snapshot;
    localStorage.setItem("buyer-funding-flow-progress", JSON.stringify(store));
  }
  if (!(await apiBackendAvailable())) return;
  try {
    await fetch(`/api/flow-state/${encodeURIComponent(snapshot.fileCode)}`, {
      method: "PUT",
      headers: apiRequestHeaders(true),
      body: JSON.stringify(snapshot)
    });
  } catch {
    // Offline — local cache remains.
  }
}

export async function fetchBuyerFlowProgress(fileCode: string): Promise<BuyerFlowProgressSnapshot | undefined> {
  const code = fileCode.trim().toUpperCase();
  if (await apiBackendAvailable()) {
    try {
      const response = await fetch(`/api/flow-state/${encodeURIComponent(code)}`);
      if (response.ok) {
        const remote = (await response.json()) as BuyerFlowProgressSnapshot | null;
        if (remote) return remote;
      }
    } catch {
      // fall through
    }
  }
  if (typeof localStorage === "undefined") return undefined;
  try {
    const store = JSON.parse(localStorage.getItem("buyer-funding-flow-progress") || "{}") as Record<
      string,
      BuyerFlowProgressSnapshot
    >;
    return store[code];
  } catch {
    return undefined;
  }
}

export async function fetchAllBuyerFlowProgress(): Promise<Record<string, BuyerFlowProgressSnapshot>> {
  if (await apiBackendAvailable()) {
    try {
      const response = await fetch("/api/flow-state", { headers: apiRequestHeaders() });
      if (response.ok) {
        return (await response.json()) as Record<string, BuyerFlowProgressSnapshot>;
      }
    } catch {
      // fall through
    }
  }
  if (typeof localStorage === "undefined") return {};
  try {
    return JSON.parse(localStorage.getItem("buyer-funding-flow-progress") || "{}") as Record<
      string,
      BuyerFlowProgressSnapshot
    >;
  } catch {
    return {};
  }
}

export async function persistEscrowPaymentRequest(record: EscrowPaymentRequestRecord): Promise<void> {
  if (typeof localStorage !== "undefined") {
    const all = JSON.parse(localStorage.getItem("buyer-funding-payment-requests") || "{}") as Record<
      string,
      EscrowPaymentRequestRecord
    >;
    all[record.fileCode.toUpperCase()] = record;
    localStorage.setItem("buyer-funding-payment-requests", JSON.stringify(all));
  }
  if (!(await apiBackendAvailable())) return;
  const response = await fetch(`/api/payment-requests/${encodeURIComponent(record.fileCode)}`, {
    method: "PUT",
    headers: apiRequestHeaders(true),
    body: JSON.stringify(record)
  });
  if (response.status === 401) {
    throw new Error("Cannot record payment request without office authentication.");
  }
}

export async function fetchEscrowPaymentRequest(fileCode: string): Promise<EscrowPaymentRequestRecord | undefined> {
  const code = fileCode.trim().toUpperCase();
  if (await apiBackendAvailable()) {
    try {
      const response = await fetch(`/api/payment-requests/${encodeURIComponent(code)}`, {
        headers: apiRequestHeaders()
      });
      if (response.ok) {
        const remote = (await response.json()) as EscrowPaymentRequestRecord | null;
        if (remote) return remote;
      }
    } catch {
      // fall through
    }
  }
  if (typeof localStorage === "undefined") return undefined;
  try {
    const all = JSON.parse(localStorage.getItem("buyer-funding-payment-requests") || "{}") as Record<
      string,
      EscrowPaymentRequestRecord
    >;
    return all[code];
  } catch {
    return undefined;
  }
}

export async function uploadSyntheticCase(reviewId = "source-mismatch"): Promise<StoredCaseManifest> {
  const response = await fetch("/api/synthetic-case", {
    method: "POST",
    headers: apiRequestHeaders(true),
    body: JSON.stringify({ reviewId })
  });
  if (!response.ok) throw new Error(`Synthetic R2 upload failed: ${response.status}`);
  return response.json();
}

export async function loadR2Case(caseId: string): Promise<BuyerFundingReview> {
  const response = await fetch(`/api/cases/${caseId}`, { headers: apiRequestHeaders() });
  if (!response.ok) throw new Error(`Synthetic R2 readback failed: ${response.status}`);
  return response.json();
}

export async function exportR2Case(caseId: string): Promise<ReceiptExportManifest> {
  const response = await fetch(`/api/cases/${caseId}/export`, {
    method: "POST",
    headers: apiRequestHeaders()
  });
  if (!response.ok) throw new Error(`Synthetic R2 receipt export failed: ${response.status}`);
  return response.json();
}

export type PersonaInquiryResponse = {
  inquiryId: string;
  referenceId: string;
};

export type DeviceOtpSendResult = {
  mode: "live" | "dev";
  maskedPhone?: string;
  status?: string;
  limitation?: string;
};

export type DeviceOtpVerifyResult = {
  mode: "live" | "dev";
  verified: boolean;
  status?: string;
  limitation?: string;
  error?: string;
};

export async function sendDeviceOtp(fileCode: string): Promise<DeviceOtpSendResult> {
  const response = await fetch("/api/device-otp/send", {
    method: "POST",
    headers: apiRequestHeaders(true),
    body: JSON.stringify({ fileCode })
  });
  const payload = (await response.json()) as DeviceOtpSendResult & { error?: string };
  if (!response.ok) {
    throw new Error(payload.error || `Device OTP send failed (${response.status})`);
  }
  return payload;
}

export async function verifyDeviceOtp(fileCode: string, code: string): Promise<DeviceOtpVerifyResult> {
  const response = await fetch("/api/device-otp/verify", {
    method: "POST",
    headers: apiRequestHeaders(true),
    body: JSON.stringify({ fileCode, code })
  });
  const payload = (await response.json()) as DeviceOtpVerifyResult & { error?: string };
  if (!response.ok && !payload.verified) {
    return { mode: payload.mode || "live", verified: false, error: payload.error || "Invalid code" };
  }
  return payload;
}

export type InviteChannelResult = { sent: boolean; id?: string; sid?: string; error?: string; skipped?: boolean };

export type InviteNotifyResult = {
  sent: boolean;
  email: InviteChannelResult;
  sms: InviteChannelResult;
};

export async function notifyBuyerInvite(
  fileCode: string,
  body: { buyerLink: string; message?: string; sentAt?: string }
): Promise<InviteNotifyResult> {
  const response = await fetch(`/api/payment-requests/${encodeURIComponent(fileCode)}/notify`, {
    method: "POST",
    headers: apiRequestHeaders(true),
    body: JSON.stringify(body)
  });
  return (await response.json()) as InviteNotifyResult;
}

export type IntegrationsStatusResponse = {
  twilioVerify: boolean;
  twilioMessaging: boolean;
  resend: boolean;
  personaServer: boolean;
  buyerPhoneConfigured: boolean;
  plaidServer: boolean;
  googleAddressValidation: boolean;
  docusign: boolean;
  docusignPartial: boolean;
};

export async function fetchIntegrationsStatus(): Promise<IntegrationsStatusResponse> {
  const response = await fetch("/api/integrations/status");
  if (!response.ok) {
    return {
      twilioVerify: false,
      twilioMessaging: false,
      resend: false,
      personaServer: false,
      buyerPhoneConfigured: false,
      plaidServer: false,
      googleAddressValidation: false,
      docusign: false,
      docusignPartial: false
    };
  }
  return (await response.json()) as IntegrationsStatusResponse;
}

export type DocuSignEnvelopeResponse = {
  mode: "live" | "simulated";
  envelopeId: string;
  signingUrl?: string;
  instructionHash: string;
  documentHash: string;
  limitation: string;
};

export async function createDocuSignEnvelope(body: {
  fileCode: string;
  returnUrl: string;
  instructionsViewedAt: string;
}): Promise<DocuSignEnvelopeResponse> {
  const response = await fetch("/api/docusign/envelope", {
    method: "POST",
    headers: apiRequestHeaders(true),
    body: JSON.stringify(body)
  });
  const payload = (await response.json()) as DocuSignEnvelopeResponse & { error?: string };
  if (!response.ok) throw new Error(payload.error || `DocuSign envelope failed (${response.status})`);
  return payload;
}

export async function fetchDocuSignEnvelopeStatus(
  envelopeId: string
): Promise<{ status: string; envelopeId: string; mode?: string }> {
  const response = await fetch(`/api/docusign/envelope/${encodeURIComponent(envelopeId)}/status`, {
    headers: apiRequestHeaders()
  });
  const payload = (await response.json()) as { status: string; error?: string };
  if (!response.ok) throw new Error(payload.error || `DocuSign status failed (${response.status})`);
  return payload as { status: string; envelopeId: string };
}

export async function completeDocuSignAcknowledgment(body: {
  fileCode: string;
  envelopeId: string;
  mode: "live" | "simulated";
  instructionVersion: string;
  instructionsViewedAt: string;
}): Promise<import("../flows/buyerFunding/types").BuyerFundingFlowDocuSignAck> {
  const response = await fetch("/api/docusign/complete", {
    method: "POST",
    headers: apiRequestHeaders(true),
    body: JSON.stringify(body)
  });
  const payload = (await response.json()) as import("../flows/buyerFunding/types").BuyerFundingFlowDocuSignAck & {
    error?: string;
  };
  if (!response.ok) throw new Error(payload.error || `DocuSign complete failed (${response.status})`);
  return payload;
}

export async function simulateDocuSignCompletion(body: {
  fileCode: string;
  envelopeId: string;
  instructionsViewedAt: string;
}): Promise<void> {
  await completeDocuSignAcknowledgment({ ...body, mode: "simulated", instructionVersion: "" });
}

export type PlaidLinkTokenResponse = { linkToken: string; expiration: string };

export async function createPlaidLinkToken(fileCode: string): Promise<PlaidLinkTokenResponse> {
  const response = await fetch("/api/plaid/link-token", {
    method: "POST",
    headers: apiRequestHeaders(true),
    body: JSON.stringify({ fileCode })
  });
  const payload = (await response.json()) as PlaidLinkTokenResponse & { error?: string };
  if (!response.ok) throw new Error(payload.error || `Plaid link token failed (${response.status})`);
  return payload;
}

export async function exchangePlaidPublicToken(body: {
  fileCode: string;
  publicToken: string;
  buyerName?: string;
}): Promise<import("../flows/buyerFunding/types").BuyerFundingFlowPlaidEvidence> {
  const response = await fetch("/api/plaid/exchange", {
    method: "POST",
    headers: apiRequestHeaders(true),
    body: JSON.stringify(body)
  });
  const payload = (await response.json()) as import("../flows/buyerFunding/types").BuyerFundingFlowPlaidEvidence & {
    error?: string;
  };
  if (!response.ok) throw new Error(payload.error || `Plaid exchange failed (${response.status})`);
  return payload;
}

export async function validatePropertyAddress(
  address: string,
  fileCode?: string
): Promise<import("../flows/buyerFunding/types").BuyerFundingFlowAddressValidation> {
  const response = await fetch("/api/address/validate", {
    method: "POST",
    headers: apiRequestHeaders(true),
    body: JSON.stringify({ address, fileCode })
  });
  const payload = (await response.json()) as import("../flows/buyerFunding/types").BuyerFundingFlowAddressValidation & {
    error?: string;
  };
  if (!response.ok) throw new Error(payload.error || `Address validation failed (${response.status})`);
  return payload;
}

export type InstructionAckChallengeResponse = {
  challenge: string;
  rpId: string;
  rpName: string;
  instructionVersion: string;
  instructionPublishedAt: string;
  fileCode: string;
  allowedOrigins: string[];
};

export async function fetchInstructionAckChallenge(
  fileCode: string,
  versionLabel: string,
  publishedAt: string
): Promise<InstructionAckChallengeResponse> {
  const params = new URLSearchParams({ fileCode, versionLabel, publishedAt });
  const response = await fetch(`/api/instruction-ack/challenge?${params}`);
  const payload = (await response.json()) as InstructionAckChallengeResponse & { error?: string };
  if (!response.ok) throw new Error(payload.error || `Challenge failed (${response.status})`);
  return payload;
}

export async function verifyInstructionAckPasskey(body: {
  fileCode: string;
  versionLabel: string;
  publishedAt: string;
  credentialId: string;
  clientDataJSON: string;
  expectedChallenge: string;
}): Promise<{ verified: boolean; credentialId?: string; limitation?: string; error?: string }> {
  const response = await fetch("/api/instruction-ack/verify", {
    method: "POST",
    headers: apiRequestHeaders(true),
    body: JSON.stringify(body)
  });
  const payload = (await response.json()) as {
    verified: boolean;
    credentialId?: string;
    limitation?: string;
    error?: string;
  };
  if (!response.ok && !payload.verified) {
    return { verified: false, error: payload.error || `Passkey verify failed (${response.status})` };
  }
  return payload;
}

export async function createPersonaInquiry(body: {
  referenceId: string;
  fileCode: string;
}): Promise<PersonaInquiryResponse> {
  const response = await fetch("/api/persona/inquiry", {
    method: "POST",
    headers: apiRequestHeaders(true),
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    throw new Error(`Persona inquiry API failed: ${response.status}`);
  }
  return response.json() as Promise<PersonaInquiryResponse>;
}

export type BuyerSessionResponse = {
  session: BuyerFundingFlowSession;
  reviewId: string;
};

export async function fetchBuyerSession(fileCode: string): Promise<BuyerSessionResponse | undefined> {
  const code = fileCode.trim().toUpperCase();
  if (await apiBackendAvailable()) {
    try {
      const response = await fetch(`/api/buyer-session/${encodeURIComponent(code)}`);
      if (response.ok) {
        return (await response.json()) as BuyerSessionResponse;
      }
    } catch {
      // fall through to fixture registry
    }
  }
  const { getBuyerFundingFlowSession } = await import("../flows/buyerFunding/registry");
  const session = getBuyerFundingFlowSession(code);
  if (!session) return undefined;
  return {
    session,
    reviewId: code === "BF-001" ? "bf-001" : `review-${code.toLowerCase()}`
  };
}

export async function listPublishedFiles(): Promise<PublishedFileSummary[]> {
  if (!(await apiBackendAvailable())) return [];
  const response = await fetch("/api/files", { headers: apiRequestHeaders() });
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Office API requires authentication.");
    }
    return [];
  }
  return (await response.json()) as PublishedFileSummary[];
}

export type PublishBuyerFileResult = {
  fileCode: string;
  reviewId: string;
  reviewNumber: string;
  buyerLink: string;
  session: BuyerFundingFlowSession;
};

export async function publishBuyerFile(
  input: PublishBuyerFileInput
): Promise<PublishBuyerFileResult> {
  const response = await fetch("/api/files", {
    method: "POST",
    headers: apiRequestHeaders(true),
    body: JSON.stringify(input)
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(payload.error || `Publish failed (${response.status})`);
  }
  return (await response.json()) as PublishBuyerFileResult;
}

export function downloadText(filename: string, text: string, type: string): void {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function readReviewsFromLocalStorage(): BuyerFundingReview[] {
  if (typeof localStorage === "undefined") return [...FIXTURES];
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as BuyerFundingReview[];
  } catch {
    // ignore
  }
  return import.meta.env.DEV ? [...FIXTURES] : [];
}

function cacheReviewsLocally(reviews: BuyerFundingReview[]): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(reviews));
}
